import React, { useState, useEffect, useMemo } from 'react';
import { X, Shuffle, BookOpen, AlertTriangle, ChevronRight, Clock } from 'lucide-react';




const PENTATEUCH = ['Genesis', 'Exodus', 'Leviticus', 'Numbers', 'Deuteronomy'];
const HISTORICAL = ['Joshua', 'Judges', 'Ruth', '1 Samuel', '2 Samuel', '1 Kings', '2 Kings', '1 Chronicles', '2 Chronicles', 'Ezra', 'Nehemiah', 'Esther'];
const WISDOM = ['Job', 'Psalms', 'Proverbs', 'Ecclesiastes', 'Song of Songs'];
const MAJOR_PROPHETS = ['Isaiah', 'Jeremiah', 'Lamentations', 'Ezekiel', 'Daniel'];
const MINOR_PROPHETS = ['Hosea', 'Joel', 'Amos', 'Obadiah', 'Jonah', 'Micah', 'Nahum', 'Habakkuk', 'Zephaniah', 'Haggai', 'Zechariah', 'Malachi'];
const MATTHEW = ['Matthew'];
const MARK = ['Mark'];
const LUKE = ['Luke'];
const JOHN_GOSPEL = ['John'];
const ACTS = ['Acts'];
const PAULINE_EPISTLES = ['Romans', '1 Corinthians', '2 Corinthians', 'Galatians', 'Ephesians', 'Philippians', 'Colossians', '1 Thessalonians', '2 Thessalonians', '1 Timothy', '2 Timothy', 'Titus', 'Philemon'];
const GENERAL_EPISTLES = ['Hebrews', 'James', '1 Peter', '2 Peter', '1 John', '2 John', '3 John', 'Jude'];
const REVELATION = ['Revelation'];

const BOOK_GROUPS = [
  { id: 'pentateuch', label: 'Pentateuch (Torah)', books: PENTATEUCH },
  { id: 'historical', label: 'Historical Books', books: HISTORICAL },
  { id: 'wisdom', label: 'Wisdom & Poetry', books: WISDOM },
  { id: 'major_prophets', label: 'Major Prophets', books: MAJOR_PROPHETS },
  { id: 'minor_prophets', label: 'Minor Prophets', books: MINOR_PROPHETS },
  { id: 'matthew', label: 'Matthew', books: MATTHEW },
  { id: 'mark', label: 'Mark', books: MARK },
  { id: 'luke', label: 'Luke', books: LUKE },
  { id: 'john_gospel', label: 'John', books: JOHN_GOSPEL },
  { id: 'acts', label: 'Acts', books: ACTS },
  { id: 'pauline_epistles', label: 'Pauline Epistles', books: PAULINE_EPISTLES },
  { id: 'general_epistles', label: 'General Epistles', books: GENERAL_EPISTLES },
  { id: 'revelation', label: 'Revelation', books: REVELATION },
];

const SELECTION_TYPES = [
  { id: 'overdue', label: 'Last Reviewed 30+ days', description: 'Random verses not reviewed in over 30 days', icon: Clock },
  { id: 'random', label: 'Random Verses', description: 'Pick verses at random from your library', icon: Shuffle },
  { id: 'missed', label: 'Missed Verses', description: 'Verses you previously answered incorrectly', icon: AlertTriangle },
  { id: 'phase', label: 'By Phase', description: 'Select verses from specific memorization phases', icon: ChevronRight },
  { id: 'group', label: 'By Book Group', description: 'Select verses from a specific section of the Bible', icon: BookOpen },
];

// Skeleton loader for ExtraReviewModal
function ExtraReviewSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="h-4 bg-muted rounded w-1/3" />
      <div className="h-8 bg-muted rounded-lg" />
      <div className="h-4 bg-muted rounded w-1/4 mt-4" />
      <div className="space-y-2">
        {[1,2,3]?.map(i => (
          <div key={i} className="h-14 bg-muted rounded-xl" />
        ))}
      </div>
    </div>
  );
}

export default function ExtraReviewModal({ isOpen, onClose, verses = [], onStartReview }) {
  const [selectionType, setSelectionType] = useState('random');
  const [verseCount, setVerseCount] = useState(10);
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [rescheduleOnResults, setRescheduleOnResults] = useState(false);
  const [updateLastReviewed, setUpdateLastReviewed] = useState(true);
  const [contentVisible, setContentVisible] = useState(false);

  // Fade-slide-in reveal after modal opens
  useEffect(() => {
    if (isOpen) {
      setContentVisible(false);
      const t = setTimeout(() => setContentVisible(true), 80);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Compute max verses based on selection type
  const availableVerses = useMemo(() => {
    if (!verses?.length) return [];
    const now = Date.now();
    const MS_PER_DAY = 86400000;
    switch (selectionType) {
      case 'random':
        return verses?.filter(v => (v?.currentPhase ?? 0) > 0);
      case 'overdue': {
        return verses?.filter(v => {
          if ((v?.currentPhase ?? 0) === 0) return false;
          const lastReview = v?.lastReviewedAt || v?.last_reviewed_at;
          if (!lastReview) return true; // never reviewed = overdue
          const days = Math.floor((now - new Date(lastReview)?.getTime()) / MS_PER_DAY);
          return days > 30;
        });
      }
      case 'missed':
        return verses?.filter(v => (v?.totalMisses ?? 0) > 0);
      case 'phase':
        if (selectedPhases?.length === 0) return verses?.filter(v => (v?.currentPhase ?? 0) > 0);
        return verses?.filter(v => selectedPhases?.includes(v?.currentPhase ?? 0));
      case 'group': {
        if (!selectedGroup) return verses?.filter(v => (v?.currentPhase ?? 0) > 0);
        const group = BOOK_GROUPS?.find(g => g?.id === selectedGroup);
        if (!group) return [];
        return verses?.filter(v => {
          const ref = v?.reference || '';
          return group?.books?.some(book => ref?.startsWith(book));
        });
      }
      default:
        return verses?.filter(v => (v?.currentPhase ?? 0) > 0);
    }
  }, [verses, selectionType, selectedPhases, selectedGroup]);

  const MAX_SESSION_VERSES = 100;
  const maxVerses = Math.min(MAX_SESSION_VERSES, Math.max(5, availableVerses?.length));
  const clampedCount = Math.min(verseCount, maxVerses);

  // Reset count when max changes
  useEffect(() => {
    if (verseCount > maxVerses) setVerseCount(maxVerses);
  }, [maxVerses]);

  const togglePhase = (phase) => {
    setSelectedPhases(prev =>
      prev?.includes(phase) ? prev?.filter(p => p !== phase) : [...prev, phase]
    );
  };

  const handleStart = () => {
    let pool = [...availableVerses];

    // Shuffle pool
    for (let i = pool?.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool?.[j], pool?.[i]];
    }

    const selected = pool?.slice(0, clampedCount);
    onStartReview?.({ verses: selected, rescheduleOnResults, updateLastReviewed });
    onClose?.();
  };

  if (!isOpen) return null;

  const availablePhases = [...new Set(verses?.filter(v => (v?.currentPhase ?? 0) > 0)?.map(v => v?.currentPhase ?? 0))]?.sort((a, b) => a - b);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-3">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal — card theme with shadow and border */}
      <div
        className={`relative bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg flex flex-col transition-all duration-300 ${
          contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
        }`}
        style={{ maxHeight: 'min(88vh, 580px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-border flex-shrink-0 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-t-2xl">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-foreground">Extra Review Session</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Configure your review session</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors border border-transparent hover:border-border shadow-sm min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-4 sm:px-5 py-4 space-y-4">
          {!contentVisible ? (
            <ExtraReviewSkeleton />
          ) : (
            <>
              {/* Verse Count Slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-foreground">Number of Verses</label>
                  <span className="text-xl font-bold text-primary">{clampedCount}</span>
                </div>
                <input
                  type="range"
                  min={5}
                  max={maxVerses}
                  step={1}
                  value={clampedCount}
                  onChange={(e) => setVerseCount(Number(e?.target?.value))}
                  className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>5</span>
                  <span className="text-muted-foreground/60">
                    {availableVerses?.length} available{availableVerses?.length > MAX_SESSION_VERSES ? ' · max 100 per session' : ''}
                  </span>
                  <span>{maxVerses}</span>
                </div>
              </div>

              {/* Selection Type */}
              <div>
                <label className="text-sm font-semibold text-foreground block mb-2">Verse Selection</label>
                <div className="grid grid-cols-1 gap-1.5">
                  {SELECTION_TYPES?.map(type => {
                    const TypeIcon = type?.icon;
                    const isSelected = selectionType === type?.id;
                    return (
                      <button
                        key={type?.id}
                        onClick={() => setSelectionType(type?.id)}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.99] ${
                          isSelected
                            ? 'border-primary bg-primary/10 text-foreground shadow-md'
                            : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30 text-foreground'
                        }`}
                      >
                        <div className={`p-1.5 rounded-lg flex-shrink-0 ${isSelected ? 'bg-primary/20' : 'bg-muted'}`}>
                          <TypeIcon className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{type?.label}</div>
                          <div className="text-xs text-muted-foreground">{type?.description}</div>
                        </div>
                        {isSelected && (
                          <div className="ml-auto w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Phase Selector (shown when selectionType === 'phase') */}
              {selectionType === 'phase' && (
                <div>
                  <label className="text-sm font-semibold text-foreground block mb-2">
                    Select Phases
                    <span className="text-xs text-muted-foreground font-normal ml-2">(leave empty for all phases)</span>
                  </label>
                  {availablePhases?.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No phases with verses found.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {availablePhases?.map(phase => {
                        const isChecked = selectedPhases?.includes(phase);
                        return (
                          <button
                            key={phase}
                            onClick={() => togglePhase(phase)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium border shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.98] ${
                              isChecked
                                ? 'bg-primary text-primary-foreground border-primary shadow-md'
                                : 'bg-card text-foreground border-border hover:border-primary/50'
                            }`}
                          >
                            Phase {phase}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Group Selector (shown when selectionType === 'group') */}
              {selectionType === 'group' && (
                <div>
                  <label className="text-sm font-semibold text-foreground block mb-2">Select Book Group</label>
                  <div className="grid grid-cols-1 gap-1.5">
                    {BOOK_GROUPS?.map(group => {
                      const count = verses?.filter(v => {
                        const ref = v?.reference || '';
                        return group?.books?.some(book => ref?.startsWith(book));
                      })?.length;
                      if (count === 0) return null;
                      const isSelected = selectedGroup === group?.id;
                      return (
                        <button
                          key={group?.id}
                          onClick={() => setSelectedGroup(group?.id)}
                          className={`flex items-center justify-between p-2.5 rounded-lg border text-left transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.99] ${
                            isSelected
                              ? 'border-primary bg-primary/10 shadow-md' : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
                          }`}
                        >
                          <span className={`text-sm font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                            {group?.label}
                          </span>
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                            {count} verse{count !== 1 ? 's' : ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Reschedule Toggles */}
              <div className="space-y-2">
                {/* Update Last-Reviewed Date */}
                <div className="flex items-center gap-3 p-2.5 bg-muted/30 rounded-lg border border-border shadow-sm">
                  <button
                    onClick={() => setUpdateLastReviewed(!updateLastReviewed)}
                    className={`relative flex-shrink-0 w-10 h-5 rounded-full transition-colors duration-200 ${
                      updateLastReviewed ? 'bg-primary' : 'bg-muted-foreground/30'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                        updateLastReviewed ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground leading-tight">
                      Update Last-Reviewed Date
                      {updateLastReviewed && (
                        <span className="ml-2 text-xs font-normal text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">Default</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {updateLastReviewed
                        ? 'Records today as the last reviewed date for each verse.'
                        : 'Last reviewed date will not be changed.'}
                    </div>
                  </div>
                </div>

                {/* Re-schedule Based on Results */}
                <div className="flex items-center gap-3 p-2.5 bg-muted/30 rounded-lg border border-border shadow-sm">
                  <button
                    onClick={() => setRescheduleOnResults(!rescheduleOnResults)}
                    className={`relative flex-shrink-0 w-10 h-5 rounded-full transition-colors duration-200 ${
                      rescheduleOnResults ? 'bg-primary' : 'bg-muted-foreground/30'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                        rescheduleOnResults ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground leading-tight">
                      Re-schedule Based on Results
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {rescheduleOnResults
                        ? 'Pushes out next review date based on your performance.'
                        : 'Next scheduled review date will not change.'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Available count notice */}
              {availableVerses?.length === 0 && (
                <div className="flex items-center gap-2 p-3 bg-warning/10 border border-warning/30 rounded-lg shadow-sm">
                  <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" />
                  <p className="text-sm text-warning">
                    No verses match the current selection. Try a different option.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer — always visible, thumb-accessible buttons */}
        <div className="flex gap-3 px-4 sm:px-5 py-3 sm:py-4 border-t border-border flex-shrink-0 bg-card rounded-b-2xl">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 rounded-full border border-border bg-muted/30 text-foreground font-semibold text-sm hover:bg-muted/60 transition-all duration-200 shadow-sm hover:shadow-md active:scale-[0.99] min-h-[48px]"
          >
            Cancel
          </button>
          <button
            onClick={handleStart}
            disabled={availableVerses?.length === 0}
            className="flex-1 py-3 px-4 rounded-full bg-gradient-to-r from-accent to-primary text-primary-foreground font-semibold text-sm hover:from-accent/90 hover:to-primary/90 transition-all duration-200 shadow-md hover:shadow-lg active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed border border-accent/20 min-h-[48px]"
          >
            Start Review ({clampedCount})
          </button>
        </div>
      </div>
    </div>
  );
}
